import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { useEnsuredForwardedRef } from 'react-use';
import { adjustTimeRange, findBestResolution } from 'components/shared/Chart';
import { getTimeRange } from 'components/shared/TimeSelect';

function useChartTime({ enabled }, forwardedRef) {
  const timeoutRef = useRef(null);
  const chartRef = useEnsuredForwardedRef(forwardedRef);
  const [chartWidth, setChartWidth] = useState(0);
  const {
    field: { value, onChange }
  } = useController({ name: 'state.query.time' });
  const timeRange = useMemo(() => getTimeRange(value), [value]);
  const resolution = useMemo(() => {
    if (!chartWidth) {
      return null;
    }

    const [from, to] = timeRange;

    const duration = new Date(to).getTime() - new Date(from).getTime();

    return findBestResolution(chartWidth, duration);
  }, [timeRange, chartWidth]);
  const [from, to] = useMemo(
    () => (resolution ? adjustTimeRange(timeRange, resolution) : timeRange),
    [timeRange, resolution]
  );
  const cancelResize = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const handleResize = useCallback(
    (chart) => {
      cancelResize();

      timeoutRef.current = setTimeout(
        () => {
          setChartWidth(chart.scales.x?.width ?? 0);
        },
        process.env.NODE_ENV !== 'test' ? 100 : 10
      );
    },
    [cancelResize]
  );
  const handleChange = useCallback(
    (event) => {
      const { min, max } = event.chart.scales.x;

      onChange({
        from: new Date(min).toISOString(),
        to: new Date(max).toISOString()
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (enabled && chartRef.current) {
      handleResize(chartRef.current);
    }
  }, [chartRef, handleResize, enabled]);
  useEffect(() => cancelResize, [cancelResize]);

  return {
    ref: chartRef,
    from,
    to,
    resolution,
    onResize: handleResize,
    onZoomComplete: handleChange
  };
}

export { useChartTime };
